import { useState, useEffect, useContext, useRef } from "react";
import { useStudioEditor } from "../../../store/studioEditorStore";
import { NavBarContext } from "../../../App";

import ErrorMessage from "../../StudioErstellen/ErrorMessage";
import classes from "../../StudioErstellen/TypeOfStudio.module.css";

const STUDIO_GROUPS = [
    {
        id: "musicRecording",
        sectionLabel: "MUSIK/AUFNAHME",
        title: "Musik & Aufnahme Studios",
        backendValues: ["Musik"], // Maps to studioTypes in backend
        tags: [
            "Tonstudio",
            "Musikstudio",
            "Aufnahmestudio",
            "Bandstudio",
            "Post-Production-Studio (Mixing & Mastering)",
            "Orchesterstudio",
            "Homestudio",
            "Podcast-/Voiceover-Studio",
            "Bühnenstudio"
        ]
    },
    {
        id: "photoVideo",
        sectionLabel: "FOTO/VIDEO STUDIOS",
        title: "Foto & Video Studios",
        backendValues: ["Foto", "Video"], // Maps to studioTypes in backend
        tags: [
            "Studio",
            "Filmstudio",
            "Loftstudio",
            "Fotostudio",
            "Videostudio",
            "TV Studio",
            "Tageslichtstudio",
            "Atelier"
        ]
    }
];

export const TypeOfStudioEditor = ({ studio, validate }) => {
    const { updateField, updatedFields } = useStudioEditor();
    const { isPhoneSize } = useContext(NavBarContext);
    const lastInitializedStudioIdRef = useRef(null);
    const [selectedCategories, setSelectedCategories] = useState([]); // Track selected group IDs
    const [selectedTags, setSelectedTags] = useState([]); // Track selected tag values
    /** Group ids where the user toggled the category card header (not only auto-added via tags). */
    const [categoriesSelectedViaHeader, setCategoriesSelectedViaHeader] = useState([]);
    const [errors, setErrors] = useState({
        studioType: ""
    });

    const isMobile = isPhoneSize;
    const [openGroups, setOpenGroups] = useState({});

    useEffect(() => {
        if (isMobile) {
            setOpenGroups({});
        } else {
            const allOpen = {};
            STUDIO_GROUPS.forEach(group => {
                allOpen[group.id] = true;
            });
            setOpenGroups(allOpen);
        }
    }, [isMobile]);

    // Effect to handle component mounting and resetting from studio data
    const extractStudioTags = (studioData) => {
        const rawTags = studioData?.tagValues ?? studioData?.tags?.items ?? [];
        return rawTags
            .map((tag) => {
                if (typeof tag === "string") return tag;
                return tag?.value ?? tag?.tagValue ?? tag?.name ?? tag?.tag?.name ?? null;
            })
            .filter(Boolean);
    };

    useEffect(() => {
        if (studio) {
            const hasUpdatedStudioTypes = Object.prototype.hasOwnProperty.call(updatedFields, "studioTypes");
            const hasUpdatedTags = Object.prototype.hasOwnProperty.call(updatedFields, "tagValues");
            // Map backend studioTypes back to selected categories
            const backendTypes = hasUpdatedStudioTypes ? updatedFields.studioTypes : (studio.studioTypes || []);
            const categoriesFromBackend = [];

            STUDIO_GROUPS.forEach(group => {
                // Check if all backendValues for this group are present in studioTypes
                const hasAllValues = group.backendValues.every(val => backendTypes.includes(val));
                if (hasAllValues && group.backendValues.length > 0) {
                    categoriesFromBackend.push(group.id);
                }
            });

            // Load tags from backend, supporting both old and new shapes
            const tagsFromBackend = hasUpdatedTags ? updatedFields.tagValues : extractStudioTags(studio);
            const isSameStudio = lastInitializedStudioIdRef.current === studio.id;
            const backendHasNoTags = tagsFromBackend.length === 0;
            const storeHasUnsavedTagEdits = hasUpdatedTags;

            if (isSameStudio && backendHasNoTags && storeHasUnsavedTagEdits) {
                return;
            }

            setSelectedCategories(categoriesFromBackend);
            setSelectedTags(tagsFromBackend);

            const explicitHeader = [];
            categoriesFromBackend.forEach((catId) => {
                const group = STUDIO_GROUPS.find((g) => g.id === catId);
                if (!group) return;
                const hasTagInGroup = tagsFromBackend.some((t) => group.tags.includes(t));
                if (!hasTagInGroup) {
                    explicitHeader.push(catId);
                }
            });
            setCategoriesSelectedViaHeader(explicitHeader);

            lastInitializedStudioIdRef.current = studio.id;
        }
        // Only hydrate local state on studio changes. User toggles update local state directly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studio]);

    // Calculate backend studioTypes from selected categories
    const getBackendStudioTypes = (categories) => {
        const backendTypes = [];
        categories.forEach(categoryId => {
            const group = STUDIO_GROUPS.find(g => g.id === categoryId);
            if (group) {
                backendTypes.push(...group.backendValues);
            }
        });
        return [...new Set(backendTypes)]; // Remove duplicates
    };

    const handleValidation = () => {
        let isValid = true;
        const newErrors = {
            studioType: ""
        };

        // At least one category OR one tag must be selected
        if (selectedCategories.length === 0 && selectedTags.length === 0) {
            newErrors.studioType = "Du musst mindestens eins auswählen.";
            isValid = false;

            // When validation fails, reset to original studio values
            if (studio?.studioTypes?.length > 0) {
                const backendTypes = studio.studioTypes || [];
                const categoriesFromBackend = [];

                STUDIO_GROUPS.forEach(group => {
                    const hasAllValues = group.backendValues.every(val => backendTypes.includes(val));
                    if (hasAllValues && group.backendValues.length > 0) {
                        categoriesFromBackend.push(group.id);
                    }
                });
                const tagsRestore = extractStudioTags(studio);
                const explicitHeader = [];
                categoriesFromBackend.forEach((catId) => {
                    const group = STUDIO_GROUPS.find((g) => g.id === catId);
                    if (!group) return;
                    const hasTagInGroup = tagsRestore.some((t) => group.tags.includes(t));
                    if (!hasTagInGroup) {
                        explicitHeader.push(catId);
                    }
                });
                setSelectedCategories(categoriesFromBackend);
                setSelectedTags(tagsRestore);
                setCategoriesSelectedViaHeader(explicitHeader);
                updateField("studioTypes", backendTypes);
                updateField("tagValues", tagsRestore);
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    const toggleCategory = (categoryId) => {
        const isCurrentlySelected = selectedCategories.includes(categoryId);
        const newSelectedCategories = isCurrentlySelected
            ? selectedCategories.filter((id) => id !== categoryId)
            : [...selectedCategories, categoryId];
        const backendTypes = getBackendStudioTypes(newSelectedCategories);

        setSelectedCategories(newSelectedCategories);

        setCategoriesSelectedViaHeader((prev) => {
            const set = new Set(prev);
            if (isCurrentlySelected) {
                set.delete(categoryId);
            } else {
                set.add(categoryId);
            }
            return [...set];
        });

        // Calculate backend studioTypes from selected categories
        // Update store with backend values when there is at least one category OR one tag
        if (newSelectedCategories.length > 0 || selectedTags.length > 0) {
            updateField("studioTypes", backendTypes);
            setErrors({ studioType: "" });
        } else {
            updateField("studioTypes", []);
            setErrors({
                studioType: "Du musst mindestens eins auswählen."
            });
            // Reset to studio values if they try to select nothing at all
            if (studio?.studioTypes?.length > 0) {
                setTimeout(() => {
                    const backendTypesRestore = studio.studioTypes || [];
                    const categoriesFromBackend = [];

                    STUDIO_GROUPS.forEach(group => {
                        const hasAllValues = group.backendValues.every(val => backendTypesRestore.includes(val));
                        if (hasAllValues && group.backendValues.length > 0) {
                            categoriesFromBackend.push(group.id);
                        }
                    });
                    setSelectedCategories(categoriesFromBackend);
                    const restoreTags = extractStudioTags(studio);
                    setSelectedTags(restoreTags);
                    const explicitHeader = [];
                    categoriesFromBackend.forEach((catId) => {
                        const group = STUDIO_GROUPS.find((g) => g.id === catId);
                        if (!group) return;
                        const tags = restoreTags;
                        const hasTagInGroup = tags.some((t) => group.tags.includes(t));
                        if (!hasTagInGroup) {
                            explicitHeader.push(catId);
                        }
                    });
                    setCategoriesSelectedViaHeader(explicitHeader);
                    updateField("studioTypes", backendTypesRestore);
                    updateField("tagValues", restoreTags);
                    setErrors({ studioType: "" });
                }, 1500);
            }
        }
    };

    const toggleTag = (value) => {
        const newSelectedTags = selectedTags.includes(value)
            ? selectedTags.filter((tag) => tag !== value)
            : [...selectedTags, value];

        setSelectedTags(newSelectedTags);
        updateField("tagValues", newSelectedTags);

        const groupForTag = STUDIO_GROUPS.find((group) => group.tags.includes(value));
        let newSelectedCategories = [...selectedCategories];

        if (groupForTag) {
            const isAdding = !selectedTags.includes(value);

            if (isAdding) {
                if (!newSelectedCategories.includes(groupForTag.id)) {
                    newSelectedCategories.push(groupForTag.id);
                    setSelectedCategories(newSelectedCategories);
                    const backendTypes = getBackendStudioTypes(newSelectedCategories);
                    updateField("studioTypes", backendTypes);
                }
            } else {
                const tagsLeftInGroup = newSelectedTags.filter((t) =>
                    groupForTag.tags.includes(t)
                );
                if (
                    tagsLeftInGroup.length === 0 &&
                    newSelectedCategories.includes(groupForTag.id) &&
                    !categoriesSelectedViaHeader.includes(groupForTag.id)
                ) {
                    newSelectedCategories = newSelectedCategories.filter(
                        (id) => id !== groupForTag.id
                    );
                    setSelectedCategories(newSelectedCategories);
                    const backendTypes = getBackendStudioTypes(newSelectedCategories);
                    updateField("studioTypes", backendTypes);
                }
            }
        }

        if ((newSelectedTags.length > 0 || newSelectedCategories.length > 0) && errors.studioType) {
            setErrors({ studioType: "" });
        }
    };

    const toggleGroupOpen = (groupId) => {
        if (!isMobile) return;
        setOpenGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const getGroupSelectedCount = (group) => {
        const tagSet = new Set(group.tags);
        return selectedTags.filter(tag => tagSet.has(tag)).length;
    };

    const isCategorySelected = (categoryId) => {
        return selectedCategories.includes(categoryId);
    };

    useEffect(() => {
        if (validate && validate.current) {
            validate.current = handleValidation;
        }
    }, [validate, selectedCategories, selectedTags]);

    return (
        <div className={classes.mainContainer}>
            <div className={classes.titleContainer}>
                <h2 className={`${classes.title} titleSemibold`}>Art des Studios</h2>
            </div>
            <div className={classes.groupsContainer}>
                {STUDIO_GROUPS.map((group) => {
                    const selectedCount = getGroupSelectedCount(group);
                    const isOpen = !isMobile || openGroups[group.id];

                    return (
                        <div key={group.id} className={classes.groupWrapper}>
                            <div className={`${classes.sectionLabel} regularLarge`}>{group.sectionLabel}</div>

                            <div
                                className={`${classes.groupCard} ${isCategorySelected(group.id) ? classes.groupCardSelected : ''}`}
                            >
                                {selectedCount > 0 && !isOpen && (
                                    <span className={`${classes.badge} semiboldXs`}>{selectedCount}</span>
                                )}
                                <div
                                    className={classes.groupHeader}
                                    onClick={() => {
                                        toggleCategory(group.id);
                                        if (isMobile) {
                                            toggleGroupOpen(group.id);
                                        }
                                    }}
                                >
                                    <div className={classes.groupHeaderLeft}>
                                        <div className={classes.groupIcon}>
                                            {group.id === "musicRecording" ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <g clipPath="url(#clip0_19760_14679)">
                                                        <path d="M15.6064 11.5449C15.9852 11.5449 16.2929 11.8524 16.293 12.2314C16.293 15.0958 13.9629 17.4257 11.0986 17.4258H10.6865V18.6777H13.6182C13.997 18.6777 14.3047 18.9855 14.3047 19.3643C14.3045 19.7432 13.9968 20.0498 13.6182 20.0498H6.38184C6.00332 20.0497 5.69652 19.7431 5.69629 19.3643C5.69629 18.9855 6.00316 18.6779 6.38184 18.6777H9.31445V17.4258H8.90234C6.03797 17.4258 3.70801 15.0958 3.70801 12.2314C3.70807 11.8525 4.015 11.5452 4.39355 11.5449C4.77231 11.5449 5.08002 11.8524 5.08008 12.2314C5.08008 14.3385 6.79492 16.0537 8.90234 16.0537H11.0986C13.2059 16.0536 14.9209 14.3385 14.9209 12.2314C14.921 11.8525 15.2278 11.5451 15.6064 11.5449ZM10 -0.0498047C12.3735 -0.0498047 14.3047 1.88129 14.3047 4.25488V10.9746C14.3045 13.3479 12.3734 15.2783 10 15.2783C7.62658 15.2781 5.69629 13.3471 5.69629 10.9736V4.25488C5.69629 1.88148 7.62664 -0.0495975 10 -0.0498047ZM10 1.32227C8.38358 1.32244 7.06836 2.63871 7.06836 4.25488V10.9736C7.06836 12.5904 8.38357 13.9061 10 13.9062C11.6166 13.9062 12.9326 12.5906 12.9326 10.9736V4.25488C12.9326 2.6386 11.6166 1.32227 10 1.32227Z" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.1" />
                                                    </g>
                                                    <defs>
                                                        <clipPath id="clip0_19760_14679">
                                                            <rect width="20" height="20" fill="white" />
                                                        </clipPath>
                                                    </defs>
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M11.6616 3.33203C11.9622 3.33203 12.2573 3.41335 12.5155 3.56739C12.7737 3.72144 12.9854 3.94245 13.1282 4.20703L13.5332 4.95703C13.676 5.22161 13.8878 5.44263 14.146 5.59667C14.4042 5.75071 14.6992 5.83204 14.9999 5.83203H16.6641C17.1061 5.83203 17.53 6.00763 17.8426 6.32019C18.1551 6.63275 18.3307 7.05667 18.3307 7.4987V14.9987C18.3307 15.4407 18.1551 15.8646 17.8426 16.1772C17.53 16.4898 17.1061 16.6654 16.6641 16.6654H3.33073C2.8887 16.6654 2.46478 16.4898 2.15222 16.1772C1.83966 15.8646 1.66406 15.4407 1.66406 14.9987V7.4987C1.66406 7.05667 1.83966 6.63275 2.15222 6.32019C2.46478 6.00763 2.8887 5.83203 3.33073 5.83203H4.9949C5.29525 5.83205 5.59002 5.7509 5.84805 5.59717C6.10607 5.44344 6.31776 5.22284 6.46073 4.9587L6.86823 4.20536C7.0112 3.94122 7.22289 3.72062 7.48091 3.56689C7.73894 3.41316 8.03371 3.33201 8.33406 3.33203H11.6616Z" stroke="#1F1F1F" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M10 13.332C11.3807 13.332 12.5 12.2127 12.5 10.832C12.5 9.45132 11.3807 8.33203 10 8.33203C8.61929 8.33203 7.5 9.45132 7.5 10.832C7.5 12.2127 8.61929 13.332 10 13.332Z" stroke="#1F1F1F" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            )}
                                        </div>
                                        <div className={classes.groupTitleWrapper}>
                                            <span className={`${classes.groupTitle} titleSemibold3`}>
                                                {group.title}
                                            </span>
                                        </div>
                                    </div>

                                    <div className={classes.groupHeaderRight}>
                                        {isMobile && (
                                            <span className={classes.chevron}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                  <path fillRule="evenodd" clipRule="evenodd" d="M20.75 8.95574L11.6516 16.25L3.25 8.95574L4.35495 7.77989L11.7014 14.1574L19.6948 7.75L20.75 8.95574Z" fill="#1F1F1F"/>
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {isOpen && (
                                    <div
                                        className={classes.tagsContainer}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {group.tags.map((tag) => {
                                            const isSelected = selectedTags.includes(tag);
                                            return (
                                                <button
                                                    key={tag}
                                                    type="button"
                                                    className={
                                                        isSelected
                                                            ? `${classes.tag} ${classes.tagSelected} mediumLarge`
                                                            : `${classes.tag} mediumLarge`
                                                    }
                                                    onClick={() => toggleTag(tag)}
                                                >
                                                    {tag}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {errors.studioType && <ErrorMessage>{errors.studioType}</ErrorMessage>}
            </div>
        </div>
    );
};

export default TypeOfStudioEditor;
